import { memo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface ShimmerSkeletonProps {
  className?: string;
  lines?: number;
  showAvatar?: boolean;
  showImage?: boolean;
  variant?: 'card' | 'list' | 'text' | 'profile' | 'chat-message' | 'full-page';
}

/**
 * Shimmer Skeleton Loader
 * Smart skeleton loading states with shimmer effect.
 */
export const ShimmerSkeleton = memo(({ 
  className = '',
  lines = 3,
  showAvatar = false,
  showImage = false,
  variant = 'card',
}: ShimmerSkeletonProps) => {
  const ShimmerBar = ({ width = '100%', height = 'h-4' }: { width?: string; height?: string }) => (
    <div className={cn('relative overflow-hidden rounded bg-muted', height)} style={{ width }}>
      <motion.div
        className="absolute inset-0 -skew-x-12"
        style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary) / 0.08), transparent)' }}
        animate={{ x: ['-100%', '100%'] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
      />
    </div>
  );

  const ShimmerCircle = ({ size = 'w-10 h-10' }: { size?: string }) => (
    <div className={cn('relative overflow-hidden rounded-full bg-muted flex-shrink-0', size)}>
      <motion.div
        className="absolute inset-0 -skew-x-12"
        style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary) / 0.08), transparent)' }}
        animate={{ x: ['-100%', '100%'] }}
        transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
      />
    </div>
  );

  // Full page loading skeleton for route transitions
  if (variant === 'full-page') {
    return (
      <div className={cn('min-h-screen w-full p-6 space-y-8', className)}>
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ShimmerCircle size="w-12 h-12" />
            <div className="space-y-2">
              <ShimmerBar width="150px" height="h-6" />
              <ShimmerBar width="100px" height="h-3" />
            </div>
          </div>
          <div className="flex gap-2">
            <ShimmerBar width="80px" height="h-9" />
            <ShimmerBar width="80px" height="h-9" />
          </div>
        </div>
        
        {/* Content skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 rounded-xl bg-card/50 space-y-4">
              <ShimmerBar width="60%" height="h-5" />
              <ShimmerBar width="100%" />
              <ShimmerBar width="80%" />
              <ShimmerBar width="40%" height="h-3" />
            </div>
          ))}
        </div>
        
        {/* Main content area */}
        <div className="p-6 rounded-xl bg-card/50 space-y-4">
          <ShimmerBar width="30%" height="h-6" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <ShimmerCircle size="w-8 h-8" />
                <ShimmerBar width={`${60 + Math.random() * 30}%`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Chat message skeleton for loading states
  if (variant === 'chat-message') {
    return (
      <div className={cn('space-y-4', className)}>
        {/* User message skeleton */}
        <div className="flex gap-3 justify-end">
          <div className="space-y-2 max-w-[70%]">
            <ShimmerBar width="200px" />
            <ShimmerBar width="150px" />
          </div>
          <ShimmerCircle size="w-8 h-8" />
        </div>
        
        {/* Assistant message skeleton */}
        <div className="flex gap-3">
          <ShimmerCircle size="w-8 h-8" />
          <div className="space-y-2 max-w-[70%] flex-1">
            <ShimmerBar width="100%" />
            <ShimmerBar width="90%" />
            <ShimmerBar width="75%" />
            <ShimmerBar width="60%" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'profile') {
    return (
      <div className={cn('flex items-center gap-4 p-4', className)}>
        <ShimmerCircle size="w-12 h-12" />
        <div className="space-y-2 flex-1">
          <ShimmerBar width="60%" />
          <ShimmerBar width="40%" height="h-3" />
        </div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={cn('space-y-3 p-4', className)}>
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            {showAvatar && <ShimmerCircle size="w-8 h-8" />}
            <ShimmerBar width={`${70 + Math.random() * 30}%`} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('p-4 space-y-4', className)}>
      {showImage && (
        <div className="relative overflow-hidden rounded-lg bg-muted h-40">
          <motion.div
            className="absolute inset-0 -skew-x-12"
            style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary) / 0.08), transparent)' }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
          />
        </div>
      )}
      {showAvatar && (
        <div className="flex items-center gap-3">
          <ShimmerCircle />
          <div className="space-y-2 flex-1">
            <ShimmerBar width="50%" />
            <ShimmerBar width="30%" height="h-3" />
          </div>
        </div>
      )}
      <div className="space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <ShimmerBar key={i} width={i === lines - 1 ? '60%' : '100%'} />
        ))}
      </div>
    </div>
  );
});

ShimmerSkeleton.displayName = 'ShimmerSkeleton';

/**
 * Chat Messages Loading Skeleton
 * Specialized skeleton for ChatMessages component
 */
export const ChatMessagesLoadingSkeleton = memo(({ messageCount = 3 }: { messageCount?: number }) => {
  return (
    <div className="space-y-6 p-4 animate-fade-in">
      {Array.from({ length: messageCount }).map((_, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <ShimmerSkeleton variant="chat-message" />
        </motion.div>
      ))}
    </div>
  );
});

ChatMessagesLoadingSkeleton.displayName = 'ChatMessagesLoadingSkeleton';
